import React, { useState, useEffect, useMemo, useCallback, useRef, Component } from 'react';
import SilentFrame from './SilentFrame.jsx';
import { geoEquirectangular, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import worldTopo from '../assets/world-110m.json';

// ── Theme ─────────────────────────────────────────────────────────────────────
const TK = {
  bg: '#ECEAE3',
  surface: '#FBFAF6',
  card: '#FFFFFF',
  border: '#ECE7DB',
  text: '#1B1B23',
  textMid: '#6B7280',
  textDim: '#A39C8B',
  accent: '#0E9488',
  accentDark: '#0E7490',
  amber: '#C8860B',
  red: '#B91C1C',
  hf: "'Space Grotesk', sans-serif",
  bf: "'Hanken Grotesk', sans-serif",
  mf: "'Space Mono', monospace",
};

const ANIM_CSS = `
@keyframes npScrnA{0%{opacity:.5;transform:translateY(12px)}100%{opacity:1;transform:translateY(0)}}
@keyframes npScrnB{0%{opacity:.5;transform:translateY(12px)}100%{opacity:1;transform:translateY(0)}}
@keyframes npItemIn{0%{opacity:0;transform:translateX(-14px)}100%{opacity:1;transform:translateX(0)}}
@keyframes npPopIn{0%{opacity:0;transform:scale(.94)}100%{opacity:1;transform:scale(1)}}
@keyframes npFloatOrb{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
@keyframes npPulseDot{0%,100%{opacity:1}50%{opacity:.4}}
@keyframes npSpin{to{transform:rotate(360deg)}}
@keyframes cwFlow{to{stroke-dashoffset:-40}}
@keyframes cwPing{0%{r:6px;opacity:.7}100%{r:22px;opacity:0}}
.cw-flow{stroke-dasharray:3 7;animation:cwFlow 1.3s linear infinite}
.cw-ping{animation:cwPing 1.9s ease-out infinite}
`;

// ── Error Boundary ────────────────────────────────────────────────────────────
class ScreenBoundary extends Component {
  state = { err: null };
  static getDerivedStateFromError(e) { return { err: e }; }
  render() {
    if (this.state.err) {
      return (
        <div style={{ background: '#FEF9EE', border: `1px solid ${TK.amber}44`, borderRadius: 13, padding: '14px 16px', marginTop: 18, fontSize: 13, color: '#92400E', fontFamily: TK.mf }}>
          Screen could not render — tap Continue to proceed.
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Maxwell-Boltzmann curve ───────────────────────────────────────────────────
function mbCurve(tidx, ea, eaCat) {
  const Tv = 0.7 + tidx * 0.14;
  const spread = 17 * Tv;
  const f = E => Math.sqrt(Math.max(E, 0)) * Math.exp(-E / spread);
  const N = 90;
  const pts = [];
  let maxf = 0, total = 0, past = 0;
  for (let k = 0; k <= N; k++) {
    const E = k * (100 / N);
    const y = f(E);
    pts.push([E, y]);
    if (y > maxf) maxf = y;
    total += y;
    if (E >= ea) past += y;
  }
  const mapX = E => 40 + (E / 100) * 550;
  const mapY = y => 262 - (y / maxf) * 226;
  const curve = 'M' + pts.map(p => `${mapX(p[0]).toFixed(1)} ${mapY(p[1]).toFixed(1)}`).join(' L ');
  const shadeFrom = thr => {
    const seg = pts.filter(p => p[0] >= thr);
    if (!seg.length) return '';
    return `M${mapX(seg[0][0]).toFixed(1)} 262 L${seg.map(p => `${mapX(p[0]).toFixed(1)} ${mapY(p[1]).toFixed(1)}`).join(' L ')} L${mapX(seg[seg.length - 1][0]).toFixed(1)} 262 Z`;
  };
  const pct = Math.round(past / total * 100);
  return {
    curve,
    shade: shadeFrom(ea),
    shadeCat: eaCat != null ? shadeFrom(eaCat) : '',
    eaX: mapX(ea).toFixed(1),
    catX: eaCat != null ? mapX(eaCat).toFixed(1) : '0',
    pct,
  };
}

// ── World Map (React SVG, locally bundled) ────────────────────────────────────
const WEST = new Set([840,124,826,250,276,380,392,36,554,528,56,578,208,300,792,620,724,352]);
const EAST = new Set([643,804,112,398,860,795,417,762,268,51,31,498,428,440,233,616,203,703,348,642,100,8,156,408,704,192,496]);

function WorldMap({ flashpoints, selectedIdx, onSelect }) {
  const W = 860, H = 430;

  const projection = useMemo(() => geoEquirectangular().fitSize([W, H], { type: 'Sphere' }), []);
  const pathFn = useMemo(() => geoPath(projection), [projection]);

  const countryPaths = useMemo(() => {
    const countries = feature(worldTopo, worldTopo.objects.countries).features;
    return countries.map(d => {
      const id = +d.id;
      const fill = WEST.has(id) ? '#A7E0D8' : EAST.has(id) ? '#F0B4B0' : '#DCD6C8';
      const stroke = WEST.has(id) ? '#5BB8AC' : EAST.has(id) ? '#D6726B' : '#C3BBA8';
      return { d: pathFn(d), fill, stroke, key: String(d.id) };
    });
  }, [pathFn]);

  const ironCurtain = useMemo(() => {
    const pts = [[12.5,54.4],[12,52],[12.5,50],[13.6,48.6],[16.5,47],[18.6,42.6]].map(c => projection(c));
    return 'M' + pts.map(p => p.join(' ')).join(' L ');
  }, [projection]);

  const arc = useCallback((a, b) => {
    const p1 = projection(a), p2 = projection(b);
    const mx = (p1[0] + p2[0]) / 2, my = (p1[1] + p2[1]) / 2;
    const dd = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    return `M${p1[0]} ${p1[1]} Q${mx} ${my - dd * 0.32} ${p2[0]} ${p2[1]}`;
  }, [projection]);

  const pins = useMemo(() =>
    (flashpoints || []).map(pt => ({ ...pt, xy: projection([pt.lon, pt.lat]) })),
    [flashpoints, projection]);

  const usaXy = useMemo(() => projection([-100, 48]), [projection]);
  const ussrXy = useMemo(() => projection([90, 66]), [projection]);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#E4EEF2" />
      <g>
        {countryPaths.map(cp => (
          <path key={cp.key} d={cp.d} fill={cp.fill} stroke={cp.stroke} strokeWidth={0.5} />
        ))}
      </g>
      <path d={ironCurtain} fill="none" stroke="#3A3A44" strokeWidth={2} strokeDasharray="5 4" opacity={0.75} />
      <path className="cw-flow" d={arc([55,57],[-98,39])} fill="none" stroke="#B91C1C" strokeWidth={2} opacity={0.8} />
      <path className="cw-flow" d={arc([-98,39],[55,57])} fill="none" stroke="#0E7490" strokeWidth={2} opacity={0.8} />
      <path className="cw-flow" d={arc([-79.5,22],[-81,28])} fill="none" stroke="#DC2626" strokeWidth={2.4} opacity={0.8} />
      <path d={arc([134,-25],[-98,39])} fill="none" stroke="#0E9488" strokeWidth={1.5} opacity={0.5} strokeDasharray="1 7" />
      <text x={usaXy[0]} y={usaXy[1]} fontFamily={TK.mf} fontSize={12} fontWeight={700} fill="#0E7490" textAnchor="middle" letterSpacing={1}>USA &amp; NATO</text>
      <text x={ussrXy[0]} y={ussrXy[1]} fontFamily={TK.mf} fontSize={12} fontWeight={700} fill="#B91C1C" textAnchor="middle" letterSpacing={1}>USSR</text>
      {pins.map((pt, idx) => (
        <g key={idx} style={{ cursor: 'pointer' }} onClick={() => onSelect(idx)}>
          <circle cx={pt.xy[0]} cy={pt.xy[1]} r={16} fill="transparent" />
          <circle className="cw-ping" cx={pt.xy[0]} cy={pt.xy[1]} r={6} fill="none" stroke={pt.fill || '#DC2626'} strokeWidth={2} />
          <circle cx={pt.xy[0]} cy={pt.xy[1]} r={5} fill={pt.fill || '#DC2626'} stroke="#fff" strokeWidth={2} />
          <text
            x={pt.xy[0]} y={pt.xy[1] - 12}
            textAnchor="middle"
            fontFamily="Space Grotesk, sans-serif"
            fontSize={12} fontWeight={700}
            fill="#23303A"
            stroke="#fff" strokeWidth={3}
            paintOrder="stroke"
          >{(pt.title || '').split(' ')[0]}</text>
        </g>
      ))}
    </svg>
  );
}

// ── Feature block (quote / compare / stats) ───────────────────────────────────
function FeatureBlock({ feat }) {
  if (!feat || !feat.type) return null;

  if (feat.type === 'quote') {
    return (
      <div style={{ marginTop: 18, background: TK.card, border: `1px solid ${TK.border}`, borderLeft: `4px solid ${TK.amber}`, borderRadius: 14, padding: '18px 20px', boxShadow: '0 6px 20px rgba(120,110,80,.07)' }}>
        <div style={{ fontFamily: TK.hf, fontWeight: 600, fontSize: 19, color: TK.text, lineHeight: 1.4 }}>{feat.text}</div>
        <div style={{ fontFamily: TK.mf, fontSize: 11, color: TK.textDim, marginTop: 9 }}>{feat.cite}</div>
      </div>
    );
  }

  if (feat.type === 'compare') {
    const side = s => (
      <div style={{ flex: 1, background: TK.card, border: `1px solid ${TK.border}`, borderTop: `3px solid ${s.color}`, borderRadius: 14, padding: '16px 18px', boxShadow: '0 4px 14px rgba(120,110,80,.05)' }}>
        <div style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 15, color: s.color, marginBottom: 11 }}>{s.title}</div>
        {(s.items || []).map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 13.5, color: '#3D4550' }}>{it}</span>
          </div>
        ))}
      </div>
    );
    return (
      <div style={{ marginTop: 18, display: 'flex', gap: 12 }}>
        {side(feat.l || { title: '', color: TK.accentDark, items: [] })}
        {side(feat.r || { title: '', color: TK.red, items: [] })}
      </div>
    );
  }

  if (feat.type === 'pairs') {
    const COLORS = { A: '#3ff2a6', T: '#ff5d7a', G: '#ffcf3f', C: '#4cc9ff' };
    return (
      <div style={{ marginTop: 18, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {(feat.pairs || []).map((p, i) => (
          <div key={i} style={{ flex: 1, minWidth: 220, background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 18, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: p.cA || COLORS[p.a] || TK.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: TK.mf, fontWeight: 700, fontSize: 22, color: '#04111f', flexShrink: 0 }}>{p.a}</div>
            <div style={{ fontFamily: TK.mf, fontSize: 18, color: TK.textMid }}>{p.sym || '='}</div>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: p.cB || COLORS[p.b] || TK.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: TK.mf, fontWeight: 700, fontSize: 22, color: '#04111f', flexShrink: 0 }}>{p.b}</div>
            <div style={{ flex: 1, fontSize: 12.5, color: TK.textMid, lineHeight: 1.4 }}>{p.note}</div>
          </div>
        ))}
      </div>
    );
  }

  if (feat.type === 'stats') {
    return (
      <div style={{ marginTop: 18, display: 'flex', gap: 12 }}>
        {(feat.stats || []).map((st, i) => (
          <div key={i} style={{ flex: 1, background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 14, padding: '16px 14px', textAlign: 'center', boxShadow: '0 4px 14px rgba(120,110,80,.05)' }}>
            <div style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 22, color: TK.accent }}>{st.n}</div>
            <div style={{ fontSize: 12, color: TK.textMid, marginTop: 4, lineHeight: 1.3 }}>{st.label}</div>
          </div>
        ))}
      </div>
    );
  }

  return null;
}

// ── Lumi callout ──────────────────────────────────────────────────────────────
function LumiBlock({ text }) {
  if (!text) return null;
  return (
    <div style={{ marginTop: 22, background: '#F0FAF8', border: '1px solid #CDEDE7', borderRadius: 15, borderTopLeftRadius: 5, padding: '14px 16px', display: 'flex', gap: 12 }}>
      <div style={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%,#5fded2,#0E9488)', animation: 'npFloatOrb 3.5s ease-in-out infinite' }} />
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: TK.text, fontFamily: TK.bf }}>Lumi</span>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: TK.accent, animation: 'npPulseDot 1.6s infinite' }} />
        </div>
        <p style={{ margin: 0, fontSize: 13, color: '#57606A', lineHeight: 1.5, fontFamily: TK.bf }}>{text}</p>
      </div>
    </div>
  );
}

// ── Screen widgets ─────────────────────────────────────────────────────────────

function TimelineWidget({ events, shown, onReveal }) {
  const visible = events.slice(0, shown);
  const hasMore = shown < events.length;
  return (
    <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {visible.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 15, alignItems: 'stretch', animation: 'npItemIn .42s cubic-bezier(.2,.8,.2,1) both' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 52 }}>
            <div style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 15, color: e.color }}>{e.year}</div>
            <div style={{ width: 2, flex: 1, background: TK.border, marginTop: 4 }} />
          </div>
          <div style={{ flex: 1, background: TK.card, border: `1px solid ${TK.border}`, borderLeft: `3px solid ${e.color}`, borderRadius: 13, padding: '13px 16px', boxShadow: '0 4px 14px rgba(120,110,80,.05)' }}>
            <div style={{ fontFamily: TK.hf, fontWeight: 600, fontSize: 15, color: TK.text }}>{e.title}</div>
            <div style={{ fontSize: 13, color: TK.textMid, lineHeight: 1.45, marginTop: 3 }}>{e.description}</div>
          </div>
        </div>
      ))}
      {hasMore && (
        <button onClick={onReveal} style={{ alignSelf: 'flex-start', background: '#E7F7F4', color: TK.accent, border: `1.5px solid #B7E6DE`, borderRadius: 11, padding: '11px 20px', fontFamily: TK.bf, fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>
          Reveal next event
        </button>
      )}
    </div>
  );
}

function SliderWidget({ armsData, idx, onIdx }) {
  const data = armsData || [];
  const d = data[Math.min(idx, data.length - 1)] || {};
  const max = 40000;
  const fmt = n => n >= 1000 ? Math.round(n / 1000) + ',000' : String(n || 0);
  const pct = n => ((n || 0) / max * 100).toFixed(1) + '%';

  return (
    <div style={{ marginTop: 18, background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 16, padding: '20px 22px', boxShadow: '0 6px 20px rgba(120,110,80,.07)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontFamily: TK.mf, fontSize: 12, color: TK.textMid }}>Year</span>
        <span style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 26, color: TK.text }}>{d.y}</span>
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: TK.accentDark }}>United States</span>
          <span style={{ fontFamily: TK.mf, fontSize: 13, color: TK.accentDark, fontWeight: 700 }}>{fmt(d.us)}</span>
        </div>
        <div style={{ height: 18, borderRadius: 6, background: '#EAF1F5', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 6, background: 'linear-gradient(90deg,#3aa6b8,#0E7490)', width: pct(d.us), transition: 'width .3s ease' }} />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: TK.red }}>Soviet Union</span>
          <span style={{ fontFamily: TK.mf, fontSize: 13, color: TK.red, fontWeight: 700 }}>{fmt(d.su)}</span>
        </div>
        <div style={{ height: 18, borderRadius: 6, background: '#FBEEEC', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 6, background: `linear-gradient(90deg,#e0635b,${TK.red})`, width: pct(d.su), transition: 'width .3s ease' }} />
        </div>
      </div>
      <input type="range" min={0} max={data.length - 1} step={1} value={idx} onChange={e => onIdx(+e.target.value)}
        style={{ width: '100%', WebkitAppearance: 'none', appearance: 'none', height: 6, borderRadius: 99, background: '#D9E6E3', outline: 'none' }} />
      <div style={{ marginTop: 10, fontSize: 13, color: '#3D4550', lineHeight: 1.5 }}>{d.note}</div>
    </div>
  );
}

function GrapherWidget({ config, temp, onTemp }) {
  const cfg = config || {};
  const ea = cfg.ea != null ? cfg.ea : 55;
  const eaCat = cfg.eaCat != null ? cfg.eaCat : null;
  const m = useMemo(() => mbCurve(temp, ea, eaCat), [temp, ea, eaCat]);
  const tempC = Math.round(150 + temp * 75);
  const tempDisplay = cfg.unit === 'C' ? `${tempC} °C` : `×${(0.7 + temp * 0.14).toFixed(2)}`;
  const catOpacity = eaCat != null ? 1 : 0;
  const catLabel = cfg.catLabel || 'With catalyst';
  const slLabel = cfg.sliderLabel || 'Temperature';

  return (
    <>
      <div style={{ marginTop: 16, background: '#14131D', borderRadius: 16, padding: '18px 18px 8px', boxShadow: '0 6px 20px rgba(20,19,29,.18)' }}>
        <svg viewBox="0 0 600 300" width="100%" style={{ display: 'block' }}>
          <path d={m.shadeCat} fill="rgba(124,92,252,.30)" stroke="none" />
          <path d={m.shade} fill="rgba(14,148,136,.38)" stroke="none" />
          <path d={m.curve} fill="none" stroke="#5fded2" strokeWidth={3} strokeLinejoin="round" />
          <line x1={40} y1={262} x2={590} y2={262} stroke="rgba(255,255,255,.35)" strokeWidth={1.5} />
          <line x1={40} y1={18} x2={40} y2={262} stroke="rgba(255,255,255,.35)" strokeWidth={1.5} />
          <line x1={m.catX} y1={26} x2={m.catX} y2={262} stroke="#9d83ff" strokeWidth={2} strokeDasharray="5 4" opacity={catOpacity} />
          <text x={m.catX} y={20} fill="#b9a6ff" fontFamily={TK.mf} fontSize={10.5} textAnchor="middle" opacity={catOpacity}>{catLabel}</text>
          <line x1={m.eaX} y1={26} x2={m.eaX} y2={262} stroke="#FBBF24" strokeWidth={2} strokeDasharray="5 4" />
          <text x={m.eaX} y={20} fill="#FBBF24" fontFamily={TK.mf} fontSize={10.5} textAnchor="middle">Activation energy</text>
          <text x={315} y={288} fill="rgba(255,255,255,.55)" fontSize={11} textAnchor="middle" fontFamily={TK.mf}>Kinetic energy</text>
          <text x={20} y={140} fill="rgba(255,255,255,.55)" fontSize={11} textAnchor="middle" fontFamily={TK.mf} transform="rotate(-90 20 140)">Particles</text>
        </svg>
      </div>
      <div style={{ marginTop: 12, background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 14, padding: '15px 18px', boxShadow: '0 4px 14px rgba(120,110,80,.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 9 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: TK.accent }}>{slLabel}</span>
          <span style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 18, color: TK.text }}>{tempDisplay}</span>
        </div>
        <input type="range" min={0} max={10} step={1} value={temp} onChange={e => onTemp(+e.target.value)}
          style={{ width: '100%', WebkitAppearance: 'none', appearance: 'none', height: 6, borderRadius: 99, background: '#D9E6E3', outline: 'none' }} />
        <div style={{ marginTop: 11, paddingTop: 11, borderTop: `1px solid #F0ECE2`, fontSize: 13.5, color: '#3D4550', lineHeight: 1.5 }}>
          About {m.pct}% of particles now have enough energy to react (energy at or past the activation line).
        </div>
      </div>
    </>
  );
}

function MapWidget({ screen, selectedPin, onSelect }) {
  const fps = screen.flashpoints || [];
  const sel = fps[selectedPin] || fps[0] || {};
  return (
    <>
      <div style={{ marginTop: 16, flexShrink: 0, background: '#E4EEF2', border: '1px solid #D6E2E8', borderRadius: 16, overflow: 'hidden' }}>
        <WorldMap flashpoints={fps} selectedIdx={selectedPin} onSelect={onSelect} />
      </div>
      <div style={{ marginTop: 14, background: TK.card, border: `1px solid ${TK.border}`, borderLeft: `4px solid ${sel.color || TK.red}`, borderRadius: 14, padding: '15px 17px', boxShadow: '0 6px 20px rgba(120,110,80,.07)', minHeight: 90 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
          <span style={{ fontFamily: TK.mf, fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: sel.color || TK.red, fontWeight: 700 }}>{sel.tag}</span>
          <span style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 16.5, color: TK.text }}>{sel.title}</span>
          <span style={{ fontFamily: TK.mf, fontSize: 11, color: TK.textDim, marginLeft: 'auto' }}>{sel.years}</span>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: '#3D4550', lineHeight: 1.55 }}>{sel.body}</p>
        {sel.aus && (
          <div style={{ marginTop: 9, display: 'inline-flex', alignItems: 'center', gap: 7, background: '#F0FAF8', border: '1px solid #CDEDE7', borderRadius: 99, padding: '5px 12px' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: TK.accent }} />
            <span style={{ fontSize: 12.5, color: '#0E7466', fontWeight: 600 }}>{sel.aus}</span>
          </div>
        )}
      </div>
    </>
  );
}

function DragMatchWidget({ pairs, dmSel, dmMatched, onSelectLeft, onSelectRight }) {
  const n = pairs.length;
  const order = useMemo(() => {
    const o = Array.from({ length: n }, (_, i) => i);
    for (let k = 0; k < 2; k++) o.push(o.shift());
    return o;
  }, [n]);

  const done = dmMatched.length === n;
  let msg = '', msgColor = TK.textMid;
  if (done) { msg = 'All matched. Nicely reasoned.'; msgColor = '#0E7466'; }
  else if (dmSel != null) { msg = 'Now tap the effect that matches.'; msgColor = TK.accent; }
  else { msg = `${dmMatched.length} of ${n} matched. Tap a factor, then its effect.`; }

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', gap: 13 }}>
        <div style={{ flex: '0 0 39%', display: 'flex', flexDirection: 'column', gap: 9 }}>
          {pairs.map((p, idx) => {
            const matched = dmMatched.includes(idx);
            const sel = dmSel === idx;
            return (
              <button key={idx} onClick={() => !matched && onSelectLeft(idx)}
                style={{ textAlign: 'left', background: matched ? '#EAF8F5' : sel ? '#E7F7F4' : TK.card, border: `1.5px solid ${matched ? TK.accent : sel ? TK.accent : TK.border}`, borderRadius: 12, padding: '13px 14px', fontFamily: TK.bf, fontWeight: 700, fontSize: 13.5, color: matched ? '#0E7466' : TK.text, cursor: matched ? 'default' : 'pointer', transition: 'all .15s ease' }}>
                {p.left}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {order.map((oi, vi) => {
            const p = pairs[oi];
            const matched = dmMatched.includes(oi);
            return (
              <button key={vi} onClick={() => {
                if (matched) return;
                if (dmSel == null) return;
                if (dmSel === oi) onSelectRight(oi);
                else onSelectRight(-1);
              }}
                style={{ textAlign: 'left', background: matched ? '#EAF8F5' : TK.card, border: `1.5px solid ${matched ? TK.accent : TK.border}`, borderRadius: 12, padding: '13px 14px', fontFamily: TK.bf, fontWeight: 500, fontSize: 13, color: matched ? '#0E7466' : '#3D4550', cursor: matched ? 'default' : 'pointer', lineHeight: 1.4, transition: 'all .15s ease' }}>
                {p.right}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: msgColor, minHeight: 18 }}>{msg}</div>
    </div>
  );
}

function ChainWidget({ chain }) {
  return (
    <div style={{ marginTop: 22, background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 16, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 6px 20px rgba(120,110,80,.07)' }}>
      {(chain || []).map((c, i) => (
        <div key={i}>
          <div style={{ fontFamily: TK.hf, fontWeight: 600, fontSize: 17, color: TK.text }}>{c.part}</div>
          <div style={{ fontFamily: TK.mf, fontSize: 11.5, color: TK.amber, marginTop: 3 }}>{c.note}</div>
        </div>
      ))}
    </div>
  );
}

function OptionsWidget({ screen, pick, onPick }) {
  const isCheck = screen.type === 'check';
  const options = screen.options || [];
  const picked = pick != null && options[pick];
  const fbGood = picked && (screen.type === 'predict' || picked.correct);

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map((o, idx) => {
          const sel = pick === idx;
          let border = TK.border;
          if (sel) border = isCheck ? (o.correct ? TK.accent : '#DC2626') : TK.accent;
          return (
            <button key={idx} onClick={() => pick == null && onPick(idx)}
              style={{ textAlign: 'left', background: TK.card, border: `1.5px solid ${border}`, borderRadius: 13, padding: '15px 17px', fontFamily: TK.bf, fontWeight: 600, fontSize: 14.5, color: '#2B2F36', cursor: pick == null ? 'pointer' : 'default', lineHeight: 1.4, transition: 'all .18s ease', boxShadow: '0 2px 8px rgba(120,110,80,.05)' }}>
              {o.text}
            </button>
          );
        })}
      </div>
      {picked && (
        <div style={{ marginTop: 14, background: fbGood ? '#EAF8F5' : '#FBEEEC', border: `1px solid ${fbGood ? '#C2E8E1' : '#F3D2CC'}`, borderRadius: 13, padding: '14px 16px', display: 'flex', gap: 11, animation: 'npPopIn .3s cubic-bezier(.2,.75,.2,1)' }}>
          <div style={{ width: 24, height: 24, flexShrink: 0, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%,#5fded2,#0E9488)' }} />
          <p style={{ margin: 0, fontSize: 13.5, color: '#3D4550', lineHeight: 1.55, fontFamily: TK.bf }}>{picked.feedback}</p>
        </div>
      )}
    </div>
  );
}

function WorkedWidget({ steps, shown, onReveal, answer }) {
  const visible = steps.slice(0, shown);
  const allDone = shown >= steps.length;
  return (
    <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {visible.map((t, k) => (
        <div key={k} style={{ background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 13, padding: '15px 17px', display: 'flex', gap: 13, alignItems: 'flex-start', animation: 'npItemIn .4s ease both', boxShadow: '0 3px 12px rgba(120,110,80,.05)' }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#E7F7F4', color: TK.accent, fontFamily: TK.hf, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{k + 1}</div>
          <div style={{ fontSize: 14.5, color: '#3D4550', lineHeight: 1.55, paddingTop: 2, fontFamily: TK.bf }}>{t}</div>
        </div>
      ))}
      {!allDone && (
        <button onClick={onReveal} style={{ alignSelf: 'flex-start', background: '#E7F7F4', color: TK.accent, border: `1.5px solid #B7E6DE`, borderRadius: 11, padding: '11px 20px', fontFamily: TK.bf, fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>
          Show the next step
        </button>
      )}
      {allDone && answer && (
        <div style={{ background: '#FFF7E6', border: `1px solid #F1DDA8`, borderRadius: 13, padding: '16px 18px', animation: 'npPopIn .35s cubic-bezier(.2,.75,.2,1)' }}>
          <div style={{ fontFamily: TK.mf, fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: TK.amber, marginBottom: 5 }}>Answer</div>
          <div style={{ fontSize: 14.5, color: TK.text, lineHeight: 1.5, fontWeight: 500, fontFamily: TK.bf }}>{answer}</div>
        </div>
      )}
    </div>
  );
}

// ── Function grapher (linear + quadratic) ────────────────────────────────────
function SliderRow({ label, value, min, max, onChange, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
      <span style={{ fontFamily: TK.mf, fontSize: 12, color: TK.textMid, width: 96, flexShrink: 0 }}>{label}</span>
      <input type="range" min={min} max={max} step={1} value={value} onChange={onChange}
        style={{ flex: 1, WebkitAppearance: 'none', appearance: 'none', height: 6, borderRadius: 99, background: '#D9E6E3', outline: 'none' }} />
      <span style={{ fontFamily: TK.mf, fontWeight: 700, fontSize: 14, color: accent, width: 28, textAlign: 'right', flexShrink: 0 }}>{value}</span>
    </div>
  );
}

function FunctionWidget({ config, fLm, fLc, fQa, fQb, fQc, setFLm, setFLc, setFQa, setFQb, setFQc }) {
  const cfg = config || {};
  const kind = cfg.kind || 'linear';
  const xr = cfg.xr || 7;
  const yr = cfg.yr || 11;
  const ac = TK.accent;
  const W = 520, H = 300, px = 44, py = 18, bx = W - 20, by = H - 22;
  const ox = (px + bx) / 2, oy = (py + by) / 2;
  const mapX = x => ox + (x / xr) * ((bx - px) / 2);
  const mapY = y => oy - (y / yr) * ((by - py) / 2);

  const xInts = [], yInts = [];
  for (let i = -Math.ceil(xr); i <= Math.ceil(xr); i++) xInts.push(i);
  for (let i = -Math.ceil(yr); i <= Math.ceil(yr); i++) yInts.push(i);

  const steps = 300;
  let curvePts = '';
  for (let k = 0; k <= steps; k++) {
    const x = xr * (k / steps * 2 - 1);
    const y = kind === 'linear' ? fLm * x + fLc : fQa * x * x + fQb * x + fQc;
    curvePts += (k === 0 ? 'M' : 'L') + mapX(x).toFixed(1) + ' ' + mapY(y).toFixed(1);
  }

  let eqn;
  if (kind === 'linear') {
    eqn = 'y = ' + (fLm === 0 ? '' : fLm === 1 ? 'x' : fLm === -1 ? '−x' : fLm + 'x');
    if (fLc !== 0 || fLm === 0) eqn += fLm === 0 ? fLc : ' ' + (fLc > 0 ? '+ ' + fLc : '− ' + (-fLc));
  } else {
    eqn = 'y = ' + (fQa === 0 ? '0' : fQa === 1 ? 'x²' : fQa === -1 ? '−x²' : fQa + 'x²');
    if (fQb !== 0) eqn += ' ' + (fQb > 0 ? '+ ' : '− ') + (Math.abs(fQb) === 1 ? '' : Math.abs(fQb)) + 'x';
    if (fQc !== 0) eqn += ' ' + (fQc > 0 ? '+ ' : '− ') + Math.abs(fQc);
  }

  let readout;
  if (kind === 'linear') {
    if (fLm === 0) readout = 'A flat line. Every x gives y = ' + fLc + '.';
    else readout = 'Gradient ' + fLm + ': ' + (fLm > 0 ? 'rises' : 'falls') + ' ' + Math.abs(fLm) + ' for every 1 step across. Crosses the y‑axis at ' + fLc + '.';
  } else {
    if (fQa === 0) readout = 'With a = 0 the x² term vanishes, leaving a straight line.';
    else {
      const vx = Math.round(-fQb / (2 * fQa) * 100) / 100;
      const vy = Math.round((fQa * vx * vx + fQb * vx + fQc) * 100) / 100;
      readout = 'Opens ' + (fQa > 0 ? 'upward' : 'downward') + '. Vertex (turning point) at (' + vx + ', ' + vy + ').';
    }
  }

  const yIntDot = kind === 'linear' ? { cx: mapX(0), cy: mapY(fLc) } : null;
  const vertexDot = (kind === 'quadratic' && fQa !== 0) ? (() => {
    const vx = -fQb / (2 * fQa);
    return { cx: mapX(vx), cy: mapY(fQa * vx * vx + fQb * vx + fQc) };
  })() : null;

  return (
    <div style={{ marginTop: 18, flexShrink: 0 }}>
      <div style={{ background: '#14131D', borderRadius: '14px 14px 0 0', padding: '11px 20px', textAlign: 'center', borderBottom: '1px solid #1f2e3e' }}>
        <span style={{ fontFamily: TK.mf, fontWeight: 700, fontSize: 20, color: '#7af0df', letterSpacing: '.02em' }}>{eqn}</span>
      </div>
      <div style={{ background: '#14131D', borderRadius: '0 0 14px 14px', padding: '4px 8px 8px' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'hidden' }}>
          <defs>
            <clipPath id="fn-clip">
              <rect x={px} y={py} width={bx - px} height={by - py} />
            </clipPath>
          </defs>
          {xInts.map(i => (
            <line key={'gx' + i} x1={mapX(i)} y1={py} x2={mapX(i)} y2={by}
              stroke={i === 0 ? 'rgba(255,255,255,.50)' : 'rgba(255,255,255,.09)'} strokeWidth={i === 0 ? 1.5 : 0.8} />
          ))}
          {yInts.map(i => (
            <line key={'gy' + i} x1={px} y1={mapY(i)} x2={bx} y2={mapY(i)}
              stroke={i === 0 ? 'rgba(255,255,255,.50)' : 'rgba(255,255,255,.09)'} strokeWidth={i === 0 ? 1.5 : 0.8} />
          ))}
          {xInts.filter(i => i !== 0 && Math.abs(i) <= xr).map(i => (
            <text key={'tx' + i} x={mapX(i)} y={mapY(0) + 15} textAnchor="middle" fontFamily={TK.mf} fontSize={10} fill="rgba(255,255,255,.38)">{i}</text>
          ))}
          {yInts.filter(i => i !== 0 && Math.abs(i) <= yr && i % 2 === 0).map(i => (
            <text key={'ty' + i} x={mapX(0) - 8} y={mapY(i) + 4} textAnchor="end" fontFamily={TK.mf} fontSize={10} fill="rgba(255,255,255,.38)">{i}</text>
          ))}
          <path d={curvePts} fill="none" stroke={ac} strokeWidth={2.8} strokeLinejoin="round" strokeLinecap="round" clipPath="url(#fn-clip)" />
          {yIntDot && <circle cx={yIntDot.cx} cy={yIntDot.cy} r={5.5} fill="#FBFAF6" stroke={ac} strokeWidth={2} />}
          {vertexDot && <circle cx={vertexDot.cx} cy={vertexDot.cy} r={5.5} fill="none" stroke={ac} strokeWidth={2.5} />}
        </svg>
      </div>
      <div style={{ marginTop: 10, background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 14, padding: '16px 18px', boxShadow: '0 4px 14px rgba(120,110,80,.05)' }}>
        {kind === 'linear' && (
          <>
            <SliderRow label="m  gradient" value={fLm} min={-5} max={5} onChange={e => setFLm(+e.target.value)} accent={ac} />
            <SliderRow label="c  intercept" value={fLc} min={-5} max={5} onChange={e => setFLc(+e.target.value)} accent={ac} />
          </>
        )}
        {kind === 'quadratic' && (
          <>
            <SliderRow label="a  stretch" value={fQa} min={-3} max={3} onChange={e => setFQa(+e.target.value)} accent={ac} />
            <SliderRow label="b  shift x" value={fQb} min={-5} max={5} onChange={e => setFQb(+e.target.value)} accent={ac} />
            <SliderRow label="c  shift y" value={fQc} min={-5} max={5} onChange={e => setFQc(+e.target.value)} accent={ac} />
          </>
        )}
        <div style={{ marginTop: 8, paddingTop: 11, borderTop: `1px solid ${TK.border}`, fontSize: 13.5, color: '#3D4550', lineHeight: 1.5 }}>{readout}</div>
      </div>
    </div>
  );
}

// ── Physics simulation ─────────────────────────────────────────────────────────
const PH_SURFACES = [
  { name: 'Ice', s: 0.10, k: 0.05 },
  { name: 'Wood', s: 0.40, k: 0.30 },
  { name: 'Rubber', s: 0.70, k: 0.55 },
];
const PH_PLANETS = [
  { name: 'Earth', g: 9.8 },
  { name: 'Moon', g: 1.6 },
  { name: 'Mars', g: 3.7 },
  { name: 'Jupiter', g: 24.8 },
];

function classifyNet(nx, ny) {
  const t = 2.5;
  if (Math.abs(nx) <= t && Math.abs(ny) <= t) return 'zero';
  if (Math.abs(nx) >= Math.abs(ny)) return nx > 0 ? 'right' : 'left';
  return ny > 0 ? 'down' : 'up';
}

function phStageSvg(simX, opts) {
  const W = 600, H = 320, railY = 196, cx = 300, fy = railY - 22;
  const NEON = '#38E8D0', DIM = '#5A6B7E', GRID = '#1C2A3A';
  let s = `<defs><linearGradient id="phsky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0A0E1A"/><stop offset="1" stop-color="#121A2C"/></linearGradient><radialGradient id="phglow" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="${NEON}" stop-opacity="0.5"/><stop offset="1" stop-color="${NEON}" stop-opacity="0"/></radialGradient></defs>`;
  s += `<rect x="0" y="0" width="${W}" height="${H}" fill="url(#phsky)"/>`;
  for (let i = 0; i < 34; i++) {
    const sx = ((i * 97) % W), sy = ((i * 53) % 140) + 8, r = (i % 4 === 0) ? 1.4 : 0.8;
    s += `<circle cx="${sx}" cy="${sy}" r="${r}" fill="#3A4A63" opacity="${0.4 + (i % 3) * 0.2}"/>`;
  }
  const sp = 60, off = ((simX % sp) + sp) % sp;
  for (let gx = -1; gx <= W / sp + 1; gx++) {
    const X = (gx * sp - off).toFixed(1);
    s += `<line x1="${X}" y1="150" x2="${X}" y2="${H}" stroke="${GRID}" stroke-width="1"/>`;
  }
  if (opts && opts.ground) s += `<rect x="0" y="${railY}" width="${W}" height="${H - railY}" fill="${opts.ground}" opacity="0.18"/>`;
  s += `<line x1="0" y1="${railY}" x2="${W}" y2="${railY}" stroke="${(opts && opts.railColor) || '#2C3E54'}" stroke-width="3"/>`;
  s += `<line x1="0" y1="${railY + 3}" x2="${W}" y2="${railY + 3}" stroke="#16202E" stroke-width="2"/>`;
  const baseM = Math.floor(simX / sp);
  for (let gx = -1; gx <= W / sp + 1; gx++) {
    const X = (gx * sp - off).toFixed(1), meters = Math.round((baseM + gx) * 10);
    s += `<line x1="${X}" y1="${railY}" x2="${X}" y2="${railY + 9}" stroke="${DIM}" stroke-width="1.5"/>`;
    s += `<text x="${X}" y="${railY + 24}" fill="${DIM}" font-family="Space Mono, monospace" font-size="10" text-anchor="middle">${meters}m</text>`;
  }
  s += `<ellipse cx="${cx}" cy="${railY - 2}" rx="70" ry="26" fill="url(#phglow)"/>`;
  s += `<g><rect x="${cx - 34}" y="${fy - 13}" width="62" height="26" rx="13" fill="#16263A" stroke="${NEON}" stroke-width="1.5"/>`;
  s += `<path d="M${cx + 28} ${fy - 13} L${cx + 50} ${fy} L${cx + 28} ${fy + 13} Z" fill="#16263A" stroke="${NEON}" stroke-width="1.5"/>`;
  s += `<circle cx="${cx + 4}" cy="${fy}" r="6" fill="${NEON}" opacity="0.85"/>`;
  s += `<circle cx="${cx - 16}" cy="${fy + 13}" r="4.5" fill="#0A0E1A" stroke="${DIM}" stroke-width="1.5"/>`;
  s += `<circle cx="${cx + 12}" cy="${fy + 13}" r="4.5" fill="#0A0E1A" stroke="${DIM}" stroke-width="1.5"/></g>`;
  return { s, W, H, cx, fy, railY };
}

function phArrowSvg(x0, y, len, color, label, dir) {
  if (len < 2) return label ? `<text x="${x0}" y="${y - 12}" fill="${color}" font-family="Space Mono, monospace" font-size="12" font-weight="700" text-anchor="middle">${label}</text>` : '';
  const x1 = x0 + dir * len;
  let s = `<line x1="${x0}" y1="${y}" x2="${x1.toFixed(1)}" y2="${y}" stroke="${color}" stroke-width="4" stroke-linecap="round"/>`;
  s += `<path d="M${x1.toFixed(1)} ${y} l${-dir * 11} -7 l0 14 Z" fill="${color}"/>`;
  if (label) s += `<text x="${((x0 + x1) / 2).toFixed(1)}" y="${y - 11}" fill="${color}" font-family="Space Mono, monospace" font-size="12" font-weight="700" text-anchor="middle">${label}</text>`;
  return s;
}

function phGaugesSvg(v, extraLabel, extraVal, extraColor, vmax) {
  const W = 600, NEON = '#38E8D0', VIO = '#8B7BFF', DIM = '#5A6B7E';
  const gw = 200, gx = 24, gy = 24, vfrac = Math.min(1, Math.abs(v) / (vmax || 90));
  let s = `<rect x="${gx}" y="${gy}" width="${gw}" height="9" rx="4.5" fill="#1C2A3A"/>`;
  s += `<rect x="${gx}" y="${gy}" width="${(gw * vfrac).toFixed(1)}" height="9" rx="4.5" fill="${NEON}"/>`;
  s += `<text x="${gx}" y="${gy - 7}" fill="${DIM}" font-family="Space Mono, monospace" font-size="10" letter-spacing="1">SPEED</text>`;
  s += `<text x="${gx + gw + 10}" y="${gy + 9}" fill="${NEON}" font-family="Space Mono, monospace" font-size="13" font-weight="700">${Math.abs(v).toFixed(1)} m/s</text>`;
  s += `<text x="${W - 24}" y="${gy + 2}" fill="${DIM}" font-family="Space Mono, monospace" font-size="10" letter-spacing="1" text-anchor="end">${extraLabel}</text>`;
  s += `<text x="${W - 24}" y="${gy + 23}" fill="${extraColor || VIO}" font-family="Space Grotesk, sans-serif" font-size="20" font-weight="700" text-anchor="end">${extraVal}</text>`;
  return s;
}

function phDraw(svgEl, sim, st) {
  const NEON = '#38E8D0', VIO = '#8B7BFF', RED = '#FF6B6B', GRN = '#3DDC84', ORG = '#F6A23A', DIM = '#5A6B7E';
  const mode = st.mode, m = st.phM, v = sim.v, vmax = (st.cfg && st.cfg.vmax) || 90;

  if (mode === 'weight') {
    const planets = (st.cfg && st.cfg.planets) || PH_PLANETS;
    const planet = planets[st.phPlanet] || PH_PLANETS[0];
    const g = planet.g, Wt = m * g, W = 600, H = 320;
    let s = `<defs><linearGradient id="phsky2" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0A0E1A"/><stop offset="1" stop-color="#121A2C"/></linearGradient></defs><rect width="${W}" height="${H}" fill="url(#phsky2)"/>`;
    for (let i = 0; i < 28; i++) {
      const sx = ((i * 113) % W), sy = ((i * 61) % H), r = (i % 4 === 0) ? 1.3 : 0.7;
      s += `<circle cx="${sx}" cy="${sy}" r="${r}" fill="#3A4A63" opacity="${0.35 + (i % 3) * 0.2}"/>`;
    }
    const cx = 200, top = 46;
    s += `<rect x="${cx - 46}" y="${top - 14}" width="92" height="12" rx="3" fill="#2C3E54"/>`;
    for (let i = 0; i < 6; i++) s += `<line x1="${cx - 46 + i * 16}" y1="${top - 2}" x2="${cx - 52 + i * 16}" y2="${top - 14}" stroke="#3A4F68" stroke-width="2"/>`;
    const sl = Math.min(150, 34 + Wt * 0.62), coils = 7, cw = 26;
    let sd = `M${cx} ${top}`;
    for (let i = 0; i < coils; i++) {
      const y0 = top + sl * (i / coils), y1 = top + sl * ((i + 0.5) / coils), y2 = top + sl * ((i + 1) / coils);
      sd += ` L${cx - cw} ${y0.toFixed(1)} L${cx + cw} ${y1.toFixed(1)} L${cx} ${y2.toFixed(1)}`;
    }
    s += `<path d="${sd}" fill="none" stroke="${NEON}" stroke-width="2.4" stroke-linejoin="round" opacity="0.9"/>`;
    const by = top + sl + 6, bs = Math.min(86, 42 + m * 3.2);
    s += `<rect x="${cx - bs / 2}" y="${by}" width="${bs}" height="${bs * 0.72}" rx="8" fill="#16263A" stroke="${VIO}" stroke-width="1.8"/>`;
    s += `<text x="${cx}" y="${by + bs * 0.72 / 2 + 5}" fill="${VIO}" font-family="Space Grotesk, sans-serif" font-size="17" font-weight="700" text-anchor="middle">${m} kg</text>`;
    const px = 360, py = 86;
    s += `<text x="${px}" y="${py}" fill="${DIM}" font-family="Space Mono, monospace" font-size="11" letter-spacing="1">ON ${planet.name.toUpperCase()} · g = ${g} N/kg</text>`;
    s += `<text x="${px}" y="${py + 44}" fill="#9FB0C2" font-family="Space Mono, monospace" font-size="13">mass (the stuff)</text>`;
    s += `<text x="${px}" y="${py + 72}" fill="${VIO}" font-family="Space Grotesk, sans-serif" font-size="26" font-weight="700">${m} kg <tspan font-family="Space Mono, monospace" font-size="12" fill="${DIM}">· same everywhere</tspan></text>`;
    s += `<text x="${px}" y="${py + 118}" fill="#9FB0C2" font-family="Space Mono, monospace" font-size="13">weight = m × g</text>`;
    s += `<text x="${px}" y="${py + 150}" fill="${NEON}" font-family="Space Grotesk, sans-serif" font-size="30" font-weight="700">${Wt.toFixed(1)} N</text>`;
    svgEl.innerHTML = s; return;
  }

  if (mode === 'netforce') {
    const stg = phStageSvg(sim.x, {}); let s = stg.s; const cx = stg.cx, fy = stg.fy;
    const net = st.phFR - st.phFL, a = net / m;
    s += phArrowSvg(cx - 36, fy, st.phFL * 2.0, RED, `${st.phFL} N`, -1);
    s += phArrowSvg(cx + 36, fy, st.phFR * 2.0, GRN, `${st.phFR} N`, +1);
    if (net === 0) {
      s += `<text x="${cx}" y="78" fill="${NEON}" font-family="Space Mono, monospace" font-size="13" font-weight="700" text-anchor="middle">BALANCED · net 0 N</text>`;
    } else {
      s += phArrowSvg(cx, 84, Math.abs(net) * 2.0, VIO, `net ${Math.abs(net)} N`, net > 0 ? 1 : -1);
    }
    s += phGaugesSvg(v, 'ACCELERATION', a.toFixed(2) + ' m/s²', VIO, vmax);
    svgEl.innerHTML = s; return;
  }

  if (mode === 'friction') {
    const surfaces = (st.cfg && st.cfg.surfaces) || PH_SURFACES;
    const surf = surfaces[st.phMu] || PH_SURFACES[1];
    const g = 10, W = m * g, sMax = surf.s * W, Fk = surf.k * W;
    const stg = phStageSvg(sim.x, { ground: ORG, railColor: '#6B5436' }); let s = stg.s; const cx = stg.cx, fy = stg.fy;
    s += phArrowSvg(cx + 36, fy, st.phF * 1.6, NEON, `push ${st.phF} N`, +1);
    const frLen = (sim.moving ? Fk : Math.min(st.phF, sMax)) * 1.6;
    s += phArrowSvg(cx - 36, fy, frLen, ORG, `friction ${(sim.moving ? Fk : Math.min(st.phF, sMax)).toFixed(0)} N`, -1);
    if (!sim.moving && st.phF <= sMax) {
      s += `<text x="${cx}" y="80" fill="${ORG}" font-family="Space Mono, monospace" font-size="12.5" font-weight="700" text-anchor="middle">STATIC FRICTION HOLDS IT (needs &gt; ${sMax.toFixed(0)} N)</text>`;
    } else {
      s += `<text x="${cx}" y="80" fill="${NEON}" font-family="Space Mono, monospace" font-size="12.5" font-weight="700" text-anchor="middle">SLIDING on ${surf.name.toUpperCase()}</text>`;
    }
    const a = sim.moving ? ((st.phF - Fk) / m) : 0;
    s += phGaugesSvg(v, 'ACCELERATION', a.toFixed(2) + ' m/s²', sim.moving ? NEON : DIM, vmax);
    svgEl.innerHTML = s; return;
  }

  // newton (default)
  const stg = phStageSvg(sim.x, {}); let s = stg.s; const cx = stg.cx, fy = stg.fy, F = st.phF, a = F / m;
  if (st.simRun) {
    const flen = (8 + F * 1.7) * (0.82 + Math.random() * 0.36);
    s += `<path d="M${cx - 34} ${fy - 9} L${cx - 34 - flen} ${fy} L${cx - 34} ${fy + 9} Z" fill="${VIO}" opacity="0.55"/>`;
    s += `<path d="M${cx - 34} ${fy - 5} L${cx - 34 - flen * 0.6} ${fy} L${cx - 34} ${fy + 5} Z" fill="${NEON}" opacity="0.9"/>`;
  }
  s += phArrowSvg(cx - F * 1.05, 92, F * 2.1, VIO, `F = ${F} N`, +1);
  s += phGaugesSvg(v, 'ACCELERATION', a.toFixed(2) + ' m/s²', VIO, vmax);
  s += `<text x="${stg.W - 24}" y="64" fill="${DIM}" font-family="Space Mono, monospace" font-size="11" text-anchor="end">distance ${(sim.x / 6).toFixed(0)} m</text>`;
  svgEl.innerHTML = s;
}

function PhysicsWidget({ config }) {
  const cfg = config || {};
  const mode = cfg.mode || 'newton';
  const [phF, setPhF] = useState(cfg.force != null ? cfg.force : 20);
  const [phM, setPhM] = useState(cfg.mass != null ? cfg.mass : 4);
  const [phFL, setPhFL] = useState(cfg.fL != null ? cfg.fL : 10);
  const [phFR, setPhFR] = useState(cfg.fR != null ? cfg.fR : 25);
  const [phMu, setPhMu] = useState(cfg.surface != null ? cfg.surface : 1);
  const [phPlanet, setPhPlanet] = useState(cfg.planet != null ? cfg.planet : 0);
  const [simRun, setSimRun] = useState(false);
  const svgRef = useRef(null);
  const simRef = useRef({ v: 0, x: 0, moving: false });
  const stRef = useRef({});
  stRef.current = { phF, phM, phFL, phFR, phMu, phPlanet, simRun, cfg, mode };

  useEffect(() => {
    let rafId, lastTs = null;
    const loop = (ts) => {
      const dt = lastTs ? Math.min(0.05, (ts - lastTs) / 1000) : 0;
      lastTs = ts;
      const st = stRef.current, sim = simRef.current, m = st.phM, vmax = (st.cfg && st.cfg.vmax) || 90;
      if (st.simRun && st.mode !== 'weight') {
        if (st.mode === 'netforce') {
          const a = (st.phFR - st.phFL) / m;
          sim.v = Math.max(-vmax, Math.min(vmax, sim.v + a * dt));
          sim.x += sim.v * 6 * dt;
        } else if (st.mode === 'friction') {
          const surfaces = (st.cfg && st.cfg.surfaces) || PH_SURFACES;
          const surf = surfaces[st.phMu] || PH_SURFACES[1];
          const g = 10, W = m * g, sMax = surf.s * W, Fk = surf.k * W;
          if (!sim.moving && st.phF > sMax) sim.moving = true;
          if (sim.moving) {
            const net = st.phF - Fk;
            sim.v = Math.min(vmax, sim.v + (net / m) * dt);
            if (sim.v <= 0) { sim.v = 0; sim.moving = false; }
            sim.x += sim.v * 6 * dt;
          }
        } else {
          sim.v = Math.min(vmax, sim.v + (st.phF / m) * dt);
          sim.x += sim.v * 6 * dt;
        }
      }
      if (svgRef.current) phDraw(svgRef.current, simRef.current, stRef.current);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = () => { simRef.current = { v: 0, x: 0, moving: false }; setSimRun(false); };
  const handleSurfacePick = k => { simRef.current = { v: 0, x: 0, moving: false }; setPhMu(k); setSimRun(false); };
  const handleMassChange = v => { simRef.current = { v: 0, x: 0, moving: false }; setPhM(v); setSimRun(false); };

  const surfaces = cfg.surfaces || PH_SURFACES;
  const planets = cfg.planets || PH_PLANETS;
  const vmax = cfg.vmax || 90;
  const NEON = '#38E8D0', VIO = '#8B7BFF', DIM = '#5A6B7E';

  let phReadout = '', phBadge = '';
  if (mode === 'newton') {
    phBadge = 'F = ma';
    phReadout = `Net force ${phF} N on ${phM} kg gives a = ${(phF / phM).toFixed(2)} m/s². ${simRun ? 'Speed keeps climbing while the force is on.' : 'Press Run to feel it.'}`;
  } else if (mode === 'netforce') {
    const net = phFR - phFL, a = net / phM;
    phBadge = 'ΣF = ma';
    phReadout = net === 0
      ? `Forces balance: ${phFR} N right and ${phFL} N left cancel. Net force 0, so no acceleration — the craft keeps whatever speed it had.`
      : `Net force ${Math.abs(net)} N to the ${net > 0 ? 'right' : 'left'}. a = ${a.toFixed(2)} m/s² that way.`;
  } else if (mode === 'friction') {
    const surf = surfaces[phMu] || PH_SURFACES[1];
    const W = phM * 10, sMax = surf.s * W, Fk = surf.k * W;
    const held = phF <= sMax && !simRef.current.moving;
    const a = simRef.current.moving ? ((phF - Fk) / phM) : 0;
    phBadge = 'friction';
    phReadout = held
      ? `On ${surf.name.toLowerCase()}, static friction can hold up to ${sMax.toFixed(0)} N. Your ${phF} N push is not enough — push harder.`
      : `Push ${phF} N beats friction. Once sliding, kinetic friction is ${Fk.toFixed(0)} N, so a = ${a.toFixed(2)} m/s².`;
  } else if (mode === 'weight') {
    const pl = planets[phPlanet] || PH_PLANETS[0];
    phBadge = 'W = mg';
    phReadout = `Same ${phM} kg of matter everywhere. On ${pl.name} gravity pulls at ${pl.g} N/kg, so it weighs ${(phM * pl.g).toFixed(1)} N. Mass stays; weight changes with gravity.`;
  }

  return (
    <div style={{ marginTop: 18, flexShrink: 0 }}>
      <div style={{ background: '#0A0E1A', borderRadius: '14px 14px 0 0', overflow: 'hidden' }}>
        <svg ref={svgRef} viewBox="0 0 600 320" width="100%" style={{ display: 'block' }} />
      </div>
      <div style={{ background: TK.card, border: `1px solid ${TK.border}`, borderRadius: '0 0 14px 14px', padding: '16px 18px', boxShadow: '0 4px 14px rgba(120,110,80,.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontFamily: TK.mf, fontSize: 11.5, fontWeight: 700, color: '#8B9CB0', letterSpacing: '.06em' }}>{phBadge}</span>
          {mode !== 'weight' && (
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button onClick={() => setSimRun(r => !r)}
                style={{ background: simRun ? '#1C2A3A' : NEON, color: simRun ? NEON : '#0A0E1A', border: `1.5px solid ${NEON}`, borderRadius: 9, padding: '8px 18px', fontFamily: TK.bf, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {simRun ? '⏸ Pause' : '▶ Run'}
              </button>
              <button onClick={handleReset}
                style={{ background: 'transparent', color: DIM, border: `1.5px solid #2C3E54`, borderRadius: 9, padding: '8px 14px', fontFamily: TK.bf, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Reset
              </button>
            </div>
          )}
        </div>
        {(mode === 'newton' || mode === 'friction') && (
          <SliderRow label="Force F (N)" value={phF} min={1} max={50} onChange={e => setPhF(+e.target.value)} accent={NEON} />
        )}
        {mode === 'netforce' && (
          <>
            <SliderRow label="← Push left (N)" value={phFL} min={0} max={50} onChange={e => setPhFL(+e.target.value)} accent="#FF6B6B" />
            <SliderRow label="Push right → (N)" value={phFR} min={0} max={50} onChange={e => setPhFR(+e.target.value)} accent="#3DDC84" />
          </>
        )}
        {mode !== 'weight' && (
          <SliderRow label="Mass m (kg)" value={phM} min={1} max={20} onChange={e => handleMassChange(+e.target.value)} accent={VIO} />
        )}
        {mode === 'weight' && (
          <SliderRow label="Mass m (kg)" value={phM} min={1} max={50} onChange={e => setPhM(+e.target.value)} accent={VIO} />
        )}
        {mode === 'friction' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {surfaces.map((surf, k) => (
              <button key={k} onClick={() => handleSurfacePick(k)}
                style={{ flex: 1, background: k === phMu ? '#0E2A2A' : 'transparent', color: k === phMu ? NEON : '#8B9CB0', border: `1.5px solid ${k === phMu ? NEON : '#2C3E54'}`, borderRadius: 8, padding: '7px 0', fontFamily: TK.mf, fontSize: 11, cursor: 'pointer' }}>
                {surf.name}
              </button>
            ))}
          </div>
        )}
        {mode === 'weight' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {planets.map((pl, k) => (
              <button key={k} onClick={() => setPhPlanet(k)}
                style={{ flex: 1, background: k === phPlanet ? '#1B1C36' : 'transparent', color: k === phPlanet ? '#B7ABFF' : '#8B9CB0', border: `1.5px solid ${k === phPlanet ? VIO : '#2C3E54'}`, borderRadius: 8, padding: '7px 0', fontFamily: TK.mf, fontSize: 11, cursor: 'pointer' }}>
                {pl.name}
              </button>
            ))}
          </div>
        )}
        <div style={{ fontSize: 13.5, color: '#3D4550', lineHeight: 1.5, paddingTop: 10, borderTop: `1px solid ${TK.border}` }}>{phReadout}</div>
      </div>
    </div>
  );
}

// ── FBD (Free-Body Diagram) ────────────────────────────────────────────────────
function fbdArrowSvg(x1, y1, x2, y2, color, w) {
  const ang = Math.atan2(y2 - y1, x2 - x1), hl = 10 + w * 1.4;
  const bx = x2 - Math.cos(ang) * hl, by = y2 - Math.sin(ang) * hl;
  const ox = Math.cos(ang + Math.PI / 2) * (hl * 0.52), oy = Math.sin(ang + Math.PI / 2) * (hl * 0.52);
  const ln = `<line x1="${x1}" y1="${y1}" x2="${bx.toFixed(1)}" y2="${by.toFixed(1)}" stroke="${color}" stroke-width="${w}" stroke-linecap="round"/>`;
  const hd = `<polygon points="${x2.toFixed(1)},${y2.toFixed(1)} ${(bx + ox).toFixed(1)},${(by + oy).toFixed(1)} ${(bx - ox).toFixed(1)},${(by - oy).toFixed(1)}" fill="${color}"/>`;
  return ln + hd;
}

function FbdWidget({ screen, fbdMag, onMagChange, fbdRes, onCheck }) {
  const c = screen.fbd || {}, forces = c.forces || [];
  const svgRef = useRef(null);
  const DIR = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };

  useEffect(() => {
    if (!svgRef.current) return;
    const W = 600, H = 320, cx = 300, cy = 158, base = 42, PXN = 2.0;
    let nx = 0, ny = 0;
    let p = `<rect x="0" y="0" width="${W}" height="${H}" fill="#0A0E1A"/>`;
    for (let gx = 40; gx < W; gx += 40) p += `<line x1="${gx}" y1="0" x2="${gx}" y2="${H}" stroke="#111A2C" stroke-width="1"/>`;
    for (let gy = 40; gy < H; gy += 40) p += `<line x1="0" y1="${gy}" x2="${W}" y2="${gy}" stroke="#111A2C" stroke-width="1"/>`;
    forces.forEach(f => {
      const m = fbdMag[f.id] || 0; if (m <= 0) return;
      const d = DIR[f.dir] || [0, 0]; nx += d[0] * m; ny += d[1] * m;
      const L = base + m * PXN, ex = cx + d[0] * L, ey = cy + d[1] * L;
      p += fbdArrowSvg(cx, cy, ex, ey, f.color || '#38E8D0', 5);
      const anchor = d[0] > 0 ? 'start' : (d[0] < 0 ? 'end' : 'middle');
      const tx = cx + d[0] * (L + 12), ty = cy + d[1] * (L + 24) + (d[1] > 0 ? 10 : 4);
      p += `<text x="${tx.toFixed(1)}" y="${ty.toFixed(1)}" fill="${f.color || '#38E8D0'}" font-family="'Space Mono',monospace" font-size="12.5" font-weight="700" text-anchor="${anchor}">${f.symbol || 'F'} = ${m} N</text>`;
    });
    p += `<rect x="${cx - 30}" y="${cy - 30}" width="60" height="60" rx="11" fill="#17223C" stroke="#4A5C7A" stroke-width="2"/>`;
    p += `<circle cx="${cx}" cy="${cy}" r="3.5" fill="#fff"/>`;
    if (c.object) p += `<text x="${cx}" y="${cy + 4.5}" fill="#C8D4E6" font-family="'Space Grotesk'" font-size="20" font-weight="700" text-anchor="middle">${c.object}</text>`;
    svgRef.current.innerHTML = p;
  }, [fbdMag, forces, c.object]); // eslint-disable-line react-hooks/exhaustive-deps

  let nx = 0, ny = 0;
  forces.forEach(f => { const m = fbdMag[f.id] || 0, d = DIR[f.dir] || [0, 0]; nx += d[0] * m; ny += d[1] * m; });
  const cls = classifyNet(nx, ny), balanced = cls === 'zero';
  const netMag = Math.round(Math.hypot(nx, ny));
  const netTxt = balanced ? 'Balanced · net force = 0 N' : `Net force points ${cls.toUpperCase()} · ${netMag} N`;
  const netColor = balanced ? '#38E8D0' : '#F5C451';
  const netBg = balanced ? 'rgba(56,232,208,.10)' : 'rgba(245,196,81,.10)';

  return (
    <div style={{ marginTop: 18, flexShrink: 0 }}>
      {c.prompt && <div style={{ fontSize: 14, color: '#3D4550', lineHeight: 1.5, marginBottom: 12, fontFamily: TK.bf }}>{c.prompt}</div>}
      <div style={{ background: '#0A0E1A', borderRadius: '14px 14px 0 0', overflow: 'hidden' }}>
        <svg ref={svgRef} viewBox="0 0 600 320" width="100%" style={{ display: 'block' }} />
      </div>
      <div style={{ background: TK.card, border: `1px solid ${TK.border}`, borderRadius: '0 0 14px 14px', padding: '16px 18px', boxShadow: '0 4px 14px rgba(120,110,80,.05)' }}>
        <div style={{ marginBottom: 14, padding: '8px 14px', background: netBg, border: `1.5px solid ${netColor}44`, borderRadius: 10, fontFamily: TK.mf, fontSize: 12, fontWeight: 700, color: netColor }}>
          {netTxt}
        </div>
        {forces.map(f => (
          <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
            <span style={{ fontFamily: TK.mf, fontSize: 11.5, color: TK.textMid, width: 118, flexShrink: 0 }}>{f.label}</span>
            <input type="range" min={0} max={f.max || 40} step={1} value={fbdMag[f.id] || 0}
              onChange={e => onMagChange(f.id, +e.target.value)}
              style={{ flex: 1, WebkitAppearance: 'none', appearance: 'none', height: 6, borderRadius: 99, background: '#D9E6E3', outline: 'none' }} />
            <span style={{ fontFamily: TK.mf, fontWeight: 700, fontSize: 13, color: f.color, width: 40, textAlign: 'right', flexShrink: 0 }}>{fbdMag[f.id] || 0} N</span>
          </div>
        ))}
        <button onClick={onCheck}
          style={{ marginTop: 6, width: '100%', background: 'linear-gradient(135deg,#19c4b3,#0E9488)', color: '#fff', border: 'none', borderRadius: 11, padding: '14px 0', fontFamily: TK.bf, fontWeight: 700, fontSize: 14.5, cursor: 'pointer', boxShadow: '0 6px 18px rgba(14,148,136,.22)' }}>
          Check my diagram
        </button>
        {fbdRes && (
          <div style={{ marginTop: 12, background: fbdRes.ok ? '#E7F7F4' : '#FFF3E8', border: `1px solid ${fbdRes.ok ? '#B7E6DE' : '#F1C99A'}`, borderRadius: 12, padding: '14px 16px', animation: 'npPopIn .3s ease' }}>
            <div style={{ fontFamily: TK.mf, fontSize: 11, fontWeight: 700, color: fbdRes.ok ? '#0E9488' : '#C2410C', marginBottom: 6 }}>{fbdRes.ok ? '✓ Correct' : 'Not yet'}</div>
            <div style={{ fontSize: 13.5, color: '#3D4550', lineHeight: 1.5, fontFamily: TK.bf }}>{fbdRes.msg}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Universal Lumi coach ───────────────────────────────────────────────────────
function getCoachCfg(screen) {
  if (!screen) return null;
  if (Array.isArray(screen.coach) && screen.coach.length) return { tiers: screen.coach, demo: screen.demo || null };
  const sub = screen[screen.type];
  if (sub && Array.isArray(sub.coach) && sub.coach.length) return { tiers: sub.coach, demo: sub.demo || null };
  if (screen.type === 'fbd') return {
    tiers: [
      'Start with what is certain. Gravity always pulls down, so there is always a weight arrow. Is anything stopping the object moving the way it is pushed? Surfaces push back. Which arrows must be on the diagram?',
      'Decide the forces first, then ask what the motion is doing: speeding up, slowing down, or staying steady. That tells you whether the arrows should balance or leave a leftover.',
      'Set the forces that genuinely act and leave the rest at zero, then size them so the net force matches the motion. Tap Check and read why it works.',
    ],
    demo: (screen.fbd && screen.fbd.demo) || null,
  };
  return null;
}

function LumiCoachWidget({ screen, coachN, onAsk, onApply, nudge }) {
  const cfg = getCoachCfg(screen);
  if (!cfg) return null;
  const tiers = cfg.tiers, shown = tiers.slice(0, coachN);
  const coachMore = coachN < tiers.length;
  const hasSetup = !!(cfg.demo && Object.keys(cfg.demo).length);
  const btnLabel = coachMore ? (coachN === 0 ? "I'm stuck — help me think it through" : 'Show me the next step') : (hasSetup ? 'Set it up for me' : null);

  return (
    <div style={{ marginTop: 13, background: '#F0FAF8', border: '1px solid #CDEDE7', borderRadius: 15, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: shown.length > 0 ? 12 : 4 }}>
        <div style={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%,#5fded2,#0E9488)', animation: 'npFloatOrb 3.5s ease-in-out infinite' }} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: TK.text, fontFamily: TK.bf }}>Lumi</span>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: TK.accent, animation: 'npPulseDot 1.6s infinite' }} />
          </div>
          <div style={{ fontSize: 12, color: '#57606A', fontFamily: TK.bf }}>here to help you think it through</div>
        </div>
      </div>
      {shown.map((t, idx) => (
        <div key={idx} style={{ marginBottom: 8, background: '#fff', border: '1px solid #CDEDE7', borderRadius: 11, padding: '10px 13px', fontSize: 13.5, color: '#3D4550', lineHeight: 1.5, fontFamily: TK.bf }}>
          <span style={{ fontFamily: TK.mf, fontSize: 10.5, color: TK.accent, fontWeight: 700, marginRight: 7 }}>STEP {idx + 1}</span>
          {t}
        </div>
      ))}
      {nudge && coachN === 0 && (
        <div style={{ marginBottom: 8, fontSize: 13, color: '#57606A', fontFamily: TK.bf, fontStyle: 'italic' }}>Not quite — want me to walk you through this one?</div>
      )}
      {btnLabel && (
        <button onClick={coachMore ? onAsk : onApply}
          style={{ marginTop: 4, width: '100%', background: coachMore ? '#E7F7F4' : 'linear-gradient(135deg,#19c4b3,#0E9488)', color: coachMore ? TK.accent : '#fff', border: coachMore ? `1.5px solid #B7E6DE` : 'none', borderRadius: 10, padding: '11px 0', fontFamily: TK.bf, fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>
          {btnLabel}
        </button>
      )}
    </div>
  );
}

// ── Full Screen renderer ───────────────────────────────────────────────────────
function ScreenBody({ screen, pick, onPick, workedStep, onWorkedReveal, timelineShown, onTimelineReveal, sliderIdx, onSliderIdx, graphTemp, onGraphTemp, dmSel, dmMatched, onDmLeft, onDmRight, selectedPin, onSelectPin, fLm, fLc, fQa, fQb, fQc, setFLm, setFLc, setFQa, setFQb, setFQc, fbdMag, onFbdMagChange, fbdRes, onFbdCheck, coachN, onCoachAsk, onCoachApply }) {
  const s = screen;

  return (
    <>
      {/* Timeline */}
      {s.type === 'timeline' && (
        <ScreenBoundary>
          <TimelineWidget events={s.events || []} shown={timelineShown} onReveal={onTimelineReveal} />
        </ScreenBoundary>
      )}

      {/* Slider */}
      {s.type === 'slider' && (
        <ScreenBoundary>
          <SliderWidget armsData={s.armsData} idx={sliderIdx} onIdx={onSliderIdx} />
        </ScreenBoundary>
      )}

      {/* Grapher */}
      {s.type === 'grapher' && (
        <ScreenBoundary>
          <GrapherWidget config={s.grapher} temp={graphTemp} onTemp={onGraphTemp} />
        </ScreenBoundary>
      )}

      {/* Map */}
      {s.type === 'map' && (
        <ScreenBoundary>
          <MapWidget screen={s} selectedPin={selectedPin} onSelect={onSelectPin} />
        </ScreenBoundary>
      )}

      {/* Feature (concept / reveal / celebrate) */}
      {(s.type === 'concept' || s.type === 'reveal' || s.type === 'celebrate') && s.feature && (
        <ScreenBoundary>
          <FeatureBlock feat={s.feature} />
        </ScreenBoundary>
      )}

      {/* Chain (reveal) */}
      {s.type === 'reveal' && s.chain && (
        <ScreenBoundary>
          <ChainWidget chain={s.chain} />
        </ScreenBoundary>
      )}

      {/* Options (check / predict) */}
      {(s.type === 'check' || s.type === 'predict') && (
        <ScreenBoundary>
          <OptionsWidget screen={s} pick={pick} onPick={onPick} />
        </ScreenBoundary>
      )}

      {/* Worked */}
      {s.type === 'worked' && (
        <ScreenBoundary>
          <WorkedWidget steps={s.steps || []} shown={workedStep} onReveal={onWorkedReveal} answer={s.answer} />
        </ScreenBoundary>
      )}

      {/* Drag match */}
      {s.type === 'dragmatch' && (
        <ScreenBoundary>
          <DragMatchWidget
            pairs={s.pairs || []}
            dmSel={dmSel}
            dmMatched={dmMatched}
            onSelectLeft={onDmLeft}
            onSelectRight={onDmRight}
          />
        </ScreenBoundary>
      )}

      {/* Function grapher */}
      {s.type === 'function' && (
        <ScreenBoundary>
          <FunctionWidget
            config={s.function}
            fLm={fLm} fLc={fLc} fQa={fQa} fQb={fQb} fQc={fQc}
            setFLm={setFLm} setFLc={setFLc} setFQa={setFQa} setFQb={setFQb} setFQc={setFQc}
          />
        </ScreenBoundary>
      )}

      {/* Physics simulation */}
      {s.type === 'physics' && (
        <ScreenBoundary>
          <PhysicsWidget config={s.physics} />
        </ScreenBoundary>
      )}

      {/* Embedded interactive (iframe) */}
      {s.type === 'embed' && s.embed && (
        <ScreenBoundary>
          <div style={{ marginTop: 16, flexShrink: 0, border: `1px solid ${TK.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 28px rgba(120,110,80,.08)' }}>
            <SilentFrame
              src={s.embed.src}
              title={s.embed.title || s.title}
              style={{ width: '100%', height: s.embed.height || 760, border: 'none', display: 'block' }}
              allow="fullscreen"
            />
          </div>
          {s.embed.caption && (
            <div style={{ marginTop: 8, textAlign: 'center', fontFamily: TK.mf, fontSize: 11, color: TK.textDim }}>{s.embed.caption}</div>
          )}
        </ScreenBoundary>
      )}

      {/* Free-body diagram */}
      {s.type === 'fbd' && (
        <ScreenBoundary>
          <FbdWidget
            screen={s}
            fbdMag={fbdMag}
            onMagChange={onFbdMagChange}
            fbdRes={fbdRes}
            onCheck={onFbdCheck}
          />
        </ScreenBoundary>
      )}

      {/* Universal Lumi coach */}
      {getCoachCfg(s) && (
        <LumiCoachWidget
          screen={s}
          coachN={coachN}
          onAsk={onCoachAsk}
          onApply={onCoachApply}
          nudge={s.type === 'fbd' && !!(fbdRes && !fbdRes.ok) && coachN === 0}
        />
      )}
    </>
  );
}

// ── Lesson player ─────────────────────────────────────────────────────────────
function LessonView({ unit, lessonIdx, initialScreenIdx, onHome, onFinish, onScreenChange }) {
  const lesson = unit.lessons[lessonIdx];
  const screens = lesson.screens || [];

  const [screenIdx, setScreenIdx] = useState(() =>
    Number.isInteger(initialScreenIdx) && initialScreenIdx < screens.length ? initialScreenIdx : 0
  );
  const [tick, setTick] = useState(0);
  const [pick, setPick] = useState(null);
  const [workedStep, setWorkedStep] = useState(1);
  const [timelineShown, setTimelineShown] = useState(3);
  const [sliderIdx, setSliderIdx] = useState(0);
  const [graphTemp, setGraphTemp] = useState(4);
  const [dmSel, setDmSel] = useState(null);
  const [dmMatched, setDmMatched] = useState([]);
  const [selectedPin, setSelectedPin] = useState(0);
  const [fLm, setFLm] = useState(1);
  const [fLc, setFLc] = useState(0);
  const [fQa, setFQa] = useState(1);
  const [fQb, setFQb] = useState(0);
  const [fQc, setFQc] = useState(0);
  const [fbdMag, setFbdMag] = useState(() => {
    const sc = screens[0];
    if (sc && sc.type === 'fbd') {
      const mg = {};
      (sc.fbd || {}).forces?.forEach(f => { mg[f.id] = f.start != null ? f.start : 0; });
      return mg;
    }
    return {};
  });
  const [fbdRes, setFbdRes] = useState(null);
  const [coachN, setCoachN] = useState(0);

  const goTo = useCallback(idx => {
    setScreenIdx(idx);
    setTick(t => t + 1);
    setPick(null);
    setWorkedStep(1);
    setTimelineShown(3);
    setSliderIdx(0);
    setGraphTemp(4);
    setDmSel(null);
    setDmMatched([]);
    setSelectedPin(0);
    setFbdRes(null);
    setCoachN(0);
    const sc = (lesson.screens || [])[idx];
    if (sc && sc.type === 'function') {
      const cfg = sc.function || {};
      if ((cfg.kind || 'linear') === 'linear') {
        setFLm(cfg.m != null ? cfg.m : 1);
        setFLc(cfg.c != null ? cfg.c : 0);
      } else {
        setFQa(cfg.a != null ? cfg.a : 1);
        setFQb(cfg.b != null ? cfg.b : 0);
        setFQc(cfg.c != null ? cfg.c : 0);
      }
    }
    if (sc && sc.type === 'fbd') {
      const mg = {};
      ((sc.fbd || {}).forces || []).forEach(f => { mg[f.id] = f.start != null ? f.start : 0; });
      setFbdMag(mg);
    } else {
      setFbdMag({});
    }
    onScreenChange?.(idx);
  }, [lesson, onScreenChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const screen = screens[screenIdx];
  if (!screen) return null;

  const isCheck = screen.type === 'check';
  const isDrag = screen.type === 'dragmatch';
  const isFbd = screen.type === 'fbd';
  const correctPicked = isCheck && pick != null && (screen.options || [])[pick]?.correct;
  const dragDone = isDrag && dmMatched.length === (screen.pairs || []).length;
  const fbdDone = isFbd && !!(fbdRes && fbdRes.ok);
  const gated = (isCheck && !correctPicked) || (isDrag && !dragDone) || (isFbd && !fbdDone);
  const isLast = screenIdx === screens.length - 1;

  const checkFbd = () => {
    const c = screen.fbd || {}, ex = c.expect || {};
    const present = new Set(ex.present || []), absent = new Set(ex.absent || []);
    const DIR = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
    let forcesOK = true;
    (c.forces || []).forEach(f => {
      const on = (fbdMag[f.id] || 0) > 0;
      if (present.has(f.id) && !on) forcesOK = false;
      if (absent.has(f.id) && on) forcesOK = false;
    });
    let nx = 0, ny = 0;
    (c.forces || []).forEach(f => { const m = fbdMag[f.id] || 0, d = DIR[f.dir] || [0, 0]; nx += d[0] * m; ny += d[1] * m; });
    const netOK = classifyNet(nx, ny) === (ex.net || 'zero');
    const ok = forcesOK && netOK;
    const msg = ok
      ? (c.solution || 'Correct — these forces, balanced this way, match the motion.')
      : (!forcesOK
        ? (c.hintForces || 'Check which forces actually act here — add the missing ones and remove any that don\'t belong.')
        : (c.hintNet || 'Right forces — now adjust their sizes so the net force matches the motion.'));
    setFbdRes({ ok, msg });
  };

  const applyCoachDemo = () => {
    const cfg = getCoachCfg(screen);
    if (!cfg || !cfg.demo) return;
    const demo = cfg.demo, T = screen.type;
    if (T === 'fbd') {
      const forces = (screen.fbd || {}).forces || [];
      const next = {};
      forces.forEach(f => { next[f.id] = demo[f.id] != null ? demo[f.id] : (fbdMag[f.id] || 0); });
      setFbdMag(next);
      setFbdRes(null);
    } else if (T === 'function') {
      if (demo.m != null) setFLm(demo.m);
      if (demo.c != null) setFLc(demo.c);
      if (demo.a != null) setFQa(demo.a);
      if (demo.b != null) setFQb(demo.b);
      if (demo.cc != null) setFQc(demo.cc);
    }
    setCoachN(cfg.tiers.length);
  };

  const handleBack = () => {
    if (screenIdx === 0) onHome();
    else goTo(screenIdx - 1);
  };

  const handleNext = () => {
    if (gated) return;
    if (isLast) onFinish();
    else goTo(screenIdx + 1);
  };

  const handleDmLeft = idx => setDmSel(idx);
  const handleDmRight = oi => {
    if (oi === -1) { setDmSel(null); return; }
    setDmMatched(prev => [...prev, oi]);
    setDmSel(null);
  };

  const nextLabel = isLast ? 'Finish lesson ✓'
    : gated ? (isFbd ? 'Check your diagram to go on' : isDrag ? 'Match them all to go on' : 'Pick an answer to go on')
    : 'Continue →';
  const nextBg = gated ? '#BFD8D4' : 'linear-gradient(135deg,#19c4b3,#0E9488)';
  const animName = tick % 2 === 0 ? 'npScrnA' : 'npScrnB';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: TK.surface }}>
      {/* Top bar */}
      <div style={{ padding: '20px 30px 0', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
        <div onClick={onHome} style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
          <div style={{ width: 24, height: 24, borderRadius: 7, background: 'linear-gradient(135deg,#19c4b3,#0E9488)' }} />
          <span style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 16, color: TK.text }}>Empirio</span>
        </div>
        <div style={{ flex: 1, display: 'flex', gap: 5 }}>
          {screens.map((_, k) => (
            <div key={k} style={{ flex: 1, height: 6, borderRadius: 99, background: k <= screenIdx ? '#14B8A6' : '#E3E0D8', transition: 'background .45s ease' }} />
          ))}
        </div>
        <span style={{ fontFamily: TK.mf, fontSize: 11, color: TK.textDim }}>{screenIdx + 1}/{screens.length}</span>
      </div>

      {/* Lesson breadcrumb */}
      <div style={{ padding: '9px 30px 4px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 18 }}>{unit.icon || '📋'}</span>
        <div style={{ fontFamily: TK.hf, fontWeight: 600, fontSize: 14, color: '#6B6557' }}>{lesson.title}</div>
        <span onClick={onHome} style={{ fontFamily: TK.mf, fontSize: 10.5, color: TK.textDim, marginLeft: 'auto', cursor: 'pointer' }}>✕ Unit menu</span>
      </div>

      {/* Screen content */}
      <div key={tick} style={{ flex: 1, minHeight: 0, padding: '22px 30px 0', display: 'flex', flexDirection: 'column', animation: `${animName} .45s cubic-bezier(.2,.75,.2,1)`, overflowY: 'auto' }}>
        <span style={{ fontFamily: TK.mf, fontSize: 11.5, letterSpacing: '.1em', textTransform: 'uppercase', color: TK.accent, fontWeight: 700 }}>{screen.eyebrow}</span>
        <h1 style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 27, color: TK.text, letterSpacing: '-.02em', lineHeight: 1.15, margin: '11px 0 13px' }}>{screen.title}</h1>
        <p style={{ margin: 0, fontSize: 16, color: '#57606A', lineHeight: 1.6, fontFamily: TK.bf }}>{screen.body}</p>

        <ScreenBody
          screen={screen}
          pick={pick} onPick={setPick}
          workedStep={workedStep} onWorkedReveal={() => setWorkedStep(s => s + 1)}
          timelineShown={timelineShown} onTimelineReveal={() => setTimelineShown(s => s + 1)}
          sliderIdx={sliderIdx} onSliderIdx={setSliderIdx}
          graphTemp={graphTemp} onGraphTemp={setGraphTemp}
          dmSel={dmSel} dmMatched={dmMatched} onDmLeft={handleDmLeft} onDmRight={handleDmRight}
          selectedPin={selectedPin} onSelectPin={setSelectedPin}
          fLm={fLm} fLc={fLc} fQa={fQa} fQb={fQb} fQc={fQc}
          setFLm={setFLm} setFLc={setFLc} setFQa={setFQa} setFQb={setFQb} setFQc={setFQc}
          fbdMag={fbdMag} onFbdMagChange={(id, v) => setFbdMag(prev => ({ ...prev, [id]: v }))}
          fbdRes={fbdRes} onFbdCheck={checkFbd}
          coachN={coachN} onCoachAsk={() => setCoachN(n => n + 1)} onCoachApply={applyCoachDemo}
        />

        {screen.sources && (
          <div style={{ marginTop: 13, fontFamily: TK.mf, fontSize: 10.5, color: '#B0A892', lineHeight: 1.5 }}>{screen.sources}</div>
        )}

        <div style={{ flex: 1 }} />
        <LumiBlock text={screen.lumi} />
        <div style={{ height: 24 }} />
      </div>

      {/* Sticky nav bar */}
      <div style={{ padding: '18px 30px 26px', display: 'flex', gap: 10, position: 'sticky', bottom: 0, background: 'linear-gradient(transparent,#FBFAF6 24%)', flexShrink: 0 }}>
        <button onClick={handleBack} style={{ background: '#EFEDE5', color: TK.textMid, border: 'none', borderRadius: 13, padding: '15px 20px', fontFamily: TK.bf, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>←</button>
        <button onClick={handleNext} style={{ flex: 1, background: nextBg, color: '#fff', border: 'none', borderRadius: 13, padding: 16, fontFamily: TK.bf, fontWeight: 800, fontSize: 15, cursor: gated ? 'default' : 'pointer', boxShadow: gated ? 'none' : '0 6px 18px rgba(14,148,136,.22)' }}>
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

// ── Home menu ─────────────────────────────────────────────────────────────────
function HomeView({ unit, completed, onLesson }) {
  const lessons = unit.lessons || [];
  const donePct = lessons.length > 0 ? Math.round(completed.length / lessons.length * 100) : 0;
  const sub = (unit.subject || '') + ' · ' + (unit.year || '').replace('year', 'Year ');

  return (
    <div style={{ padding: '30px 34px 50px', background: TK.surface, minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 24 }}>
        <div style={{ width: 24, height: 24, borderRadius: 7, background: 'linear-gradient(135deg,#19c4b3,#0E9488)' }} />
        <span style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 16, color: TK.text }}>Empirio</span>
        <span style={{ fontFamily: TK.mf, fontSize: 11, color: TK.textDim, marginLeft: 'auto' }}>{sub}</span>
      </div>

      <span style={{ fontFamily: TK.mf, fontSize: 11.5, letterSpacing: '.1em', textTransform: 'uppercase', color: TK.accent, fontWeight: 700 }}>Unit · {lessons.length} lessons</span>
      <h1 style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 33, color: TK.text, letterSpacing: '-.02em', margin: '8px 0 18px' }}>{unit.title}</h1>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 99, background: '#E7E3D8', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 99, background: 'linear-gradient(90deg,#19c4b3,#0E9488)', width: donePct + '%', transition: 'width .4s ease' }} />
        </div>
        <span style={{ fontFamily: TK.mf, fontSize: 12, color: TK.accent, fontWeight: 700 }}>{completed.length}/{lessons.length}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {lessons.map((l, idx) => {
          const done = completed.includes(idx);
          const status = done ? 'Done' : 'Start';
          const statusColor = done ? '#0E7466' : TK.textDim;
          const badgeBg = done ? TK.accent : TK.card;
          const badgeColor = done ? '#fff' : TK.textDim;
          const badgeBorder = done ? TK.accent : '#E3E0D8';

          return (
            <div key={idx} onClick={() => onLesson(idx)}
              style={{ display: 'flex', gap: 15, alignItems: 'center', background: TK.card, border: `1px solid ${TK.border}`, borderRadius: 15, padding: '15px 17px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(120,110,80,.05)', transition: 'all .18s ease' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 22px rgba(120,110,80,.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 14px rgba(120,110,80,.05)'; }}>
              <div style={{ width: 42, height: 42, flexShrink: 0, borderRadius: '50%', background: badgeBg, color: badgeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: TK.hf, fontWeight: 700, fontSize: 16, border: `2px solid ${badgeBorder}` }}>
                {done ? '✓' : idx + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: TK.hf, fontWeight: 700, fontSize: 16, color: TK.text }}>{l.title}</div>
                <div style={{ fontSize: 13, color: TK.textMid, lineHeight: 1.45, marginTop: 2 }}>{l.focus}</div>
              </div>
              <div style={{ fontFamily: TK.mf, fontSize: 11, color: statusColor, fontWeight: 700, flexShrink: 0 }}>{status}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function NativeUnitPlayer({ unitUrl, onClose }) {
  const [unit, setUnit] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [view, setView] = useState('home');
  const [lessonIdx, setLessonIdx] = useState(0);
  const [resumeScreenIdx, setResumeScreenIdx] = useState(0);
  const [completed, setCompleted] = useState([]);

  const posKey = useCallback(unitId => 'lumio_lesson_pos_' + unitId, []);
  const savePos = useCallback((unitId, pos) => {
    try {
      if (pos) localStorage.setItem(posKey(unitId), JSON.stringify(pos));
      else localStorage.removeItem(posKey(unitId));
    } catch (e) {}
  }, [posKey]);

  useEffect(() => {
    let cancelled = false;
    setUnit(null);
    setLoadErr(null);
    fetch(unitUrl)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (cancelled) return;
        let done = [];
        try {
          const s = JSON.parse(localStorage.getItem('lumio_unit_' + data.id) || '{}');
          if (Array.isArray(s.completed)) done = s.completed;
        } catch (e) {}
        setUnit(data);
        setCompleted(done);
        // Resume where the student left off in this unit, if recent enough.
        try {
          const pos = JSON.parse(localStorage.getItem(posKey(data.id)) || 'null');
          if (
            pos && pos.view === 'lesson' && Number.isInteger(pos.lessonIdx) &&
            data.lessons?.[pos.lessonIdx] && Date.now() - (pos.savedAt || 0) < 24 * 60 * 60 * 1000
          ) {
            setLessonIdx(pos.lessonIdx);
            setResumeScreenIdx(Number.isInteger(pos.screenIdx) ? pos.screenIdx : 0);
            setView('lesson');
          }
        } catch (e) {}
      })
      .catch(e => { if (!cancelled) setLoadErr(e.message); });
    return () => { cancelled = true; };
  }, [unitUrl, posKey]);

  const save = useCallback((c, unitId) => {
    try { localStorage.setItem('lumio_unit_' + unitId, JSON.stringify({ completed: c })); } catch (e) {}
  }, []);

  const handleOpenLesson = useCallback(idx => {
    setLessonIdx(idx);
    setResumeScreenIdx(0);
    setView('lesson');
    if (unit) savePos(unit.id, { view: 'lesson', lessonIdx: idx, screenIdx: 0, savedAt: Date.now() });
  }, [unit, savePos]);

  const handleHome = useCallback(() => {
    setView('home');
    if (unit) savePos(unit.id, null);
  }, [unit, savePos]);

  const handleFinish = useCallback(() => {
    setCompleted(prev => {
      const next = prev.includes(lessonIdx) ? prev : [...prev, lessonIdx];
      save(next, unit.id);
      return next;
    });
    setView('home');
    if (unit) savePos(unit.id, null);
  }, [lessonIdx, unit, save, savePos]);

  const handleScreenChange = useCallback(idx => {
    if (unit) savePos(unit.id, { view: 'lesson', lessonIdx, screenIdx: idx, savedAt: Date.now() });
  }, [unit, lessonIdx, savePos]);

  const loading = !unit && !loadErr;

  return (
    <>
      <style>{ANIM_CSS}</style>
      <style>{`input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:22px;height:22px;border-radius:50%;background:#0E9488;cursor:pointer;box-shadow:0 2px 6px rgba(14,148,136,.4)}`}</style>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', flexDirection: 'column', background: TK.bg, fontFamily: TK.bf }}>
        {/* Header with close */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', background: TK.surface, borderBottom: `1px solid ${TK.border}`, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: '#EFEDE5', border: 'none', borderRadius: 8, width: 36, height: 36, color: TK.textMid, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>←</button>
          <span style={{ marginLeft: 10, fontSize: 14, fontWeight: 700, color: TK.text, fontFamily: TK.hf }}>{unit ? unit.title : 'Loading...'}</span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', border: `3px solid ${TK.border}`, borderTopColor: TK.accent, animation: 'npSpin .8s linear infinite' }} />
              <span style={{ fontFamily: TK.mf, fontSize: 13, color: TK.textDim }}>Loading unit...</span>
            </div>
          )}
          {loadErr && (
            <div style={{ padding: 24, fontFamily: TK.mf, fontSize: 13, color: '#DC2626' }}>Failed to load: {loadErr}</div>
          )}
          {unit && view === 'home' && (
            <HomeView unit={unit} completed={completed} onLesson={handleOpenLesson} />
          )}
          {unit && view === 'lesson' && (
            <LessonView
              unit={unit}
              lessonIdx={lessonIdx}
              initialScreenIdx={resumeScreenIdx}
              onHome={handleHome}
              onFinish={handleFinish}
              onScreenChange={handleScreenChange}
            />
          )}
        </div>
      </div>
    </>
  );
}
